import React from "react";
import "../pages/LiqIntelReport.css";

export type DelinquencyRow = {
  loanPortfolio: string;
  loanAmount: string;
  totalDelinquent: string;
  perc: string;
  peers: string;
};

function parsePercentValue(input: string): number | null {
  const normalized = input.replace(/[%\s,]/g, "").trim();
  if (!normalized) return null;
  const n = Number.parseFloat(normalized);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function LiqIntelDelinquencyTable({ rows }: { rows: DelinquencyRow[] }) {
  if (rows.length === 0) return null;

  const allPctValues = rows.flatMap((row) => [
    parsePercentValue(row.perc) ?? 0,
    parsePercentValue(row.peers) ?? 0,
  ]);
  const comparisonScaleMax = Math.max(1, ...allPctValues);

  return (
    <article className="liq-identity-block liq-delinquency-box">
      <p className="liq-kpi-label">Loans and Delinquency</p>
      <div className="liq-delinquency-table-wrap">
        <table className="liq-delinquency-table">
          <thead>
            <tr>
              <th>Loan Portfolio</th>
              <th>Loan Amount</th>
              <th>Total Delinquent</th>
              <th>Perc</th>
              <th>Peers</th>
              <th>Comparison</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const delinqPct = parsePercentValue(row.perc);
              const peersPct = parsePercentValue(row.peers);
              const valid =
                delinqPct !== null && peersPct !== null && (delinqPct > 0 || peersPct > 0);
              const isTotalRow = /^total\b/i.test(row.loanPortfolio.trim());
              const delinqWidth = valid ? (delinqPct / comparisonScaleMax) * 100 : 0;
              const peersWidth = valid ? (peersPct / comparisonScaleMax) * 100 : 0;
              const delinquencyWorse = valid ? delinqPct > peersPct : false;
              return (
                <tr
                  key={`${row.loanPortfolio}-${idx}`}
                  className={isTotalRow ? "liq-delinquency-row--total" : undefined}
                >
                  <td>{row.loanPortfolio}</td>
                  <td>{row.loanAmount}</td>
                  <td>{row.totalDelinquent}</td>
                  <td
                    className={
                      delinquencyWorse
                        ? "liq-delinquency-perc liq-delinquency-perc--bad"
                        : "liq-delinquency-perc liq-delinquency-perc--good"
                    }
                  >
                    {row.perc}
                  </td>
                  <td>{row.peers}</td>
                  <td>
                    {valid ? (
                      <div className="liq-delinquency-visual">
                        <div className="liq-delinquency-track">
                          <div
                            className="liq-delinquency-bar liq-delinquency-bar--peers"
                            style={{ width: `${peersWidth}%` }}
                          />
                          <div
                            className={
                              delinquencyWorse
                                ? "liq-delinquency-bar liq-delinquency-bar--delinq-bad"
                                : "liq-delinquency-bar liq-delinquency-bar--delinq-good"
                            }
                            style={{ width: `${delinqWidth}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="liq-delinquency-na">N/A</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
}
